import { hidePurchases } from '../lib/native'

/**
 * Plan headroom, shown before it bites (v0.21.3, D-130).
 *
 * The keeper lost an evening to "adding a sibling just doesn't save": the tree had
 * quietly reached its plan's person cap, and the only signal was a rejection that
 * the UI swallowed. A limit you cannot see until it stops you is indistinguishable
 * from a broken app. So: silent while there is room, plainly visible as it nears,
 * unmissable when reached.
 *
 * Two things it is careful about:
 *  · The governing plan is the TREE OWNER's, not the viewer's — a collaborator is
 *    told whose limit it is, because "upgrade" is not an action they can take.
 *  · No purchase wording inside the store apps (Apple 3.1.1) — hidePurchases().
 */
export type Capacity = {
  persons_used: number
  persons_limit: number     // -1 = unlimited
  owner_plan: string
  is_owner: boolean
}

export default function CapacityMeter({ capacity }: { capacity?: Capacity }) {
  if (!capacity || capacity.persons_limit === -1) return null

  const { persons_used: used, persons_limit: limit, is_owner } = capacity
  const pct = Math.min(100, Math.round((used / limit) * 100))
  if (pct < 75) {
    // plenty of room — say nothing louder than a count
    return <span className="text-[11px] text-slate-600">· {used}/{limit} people</span>
  }

  const full = used >= limit
  const tone = full
    ? 'border-red-500/35 bg-red-500/[0.07] text-red-200'
    : 'border-amber-500/30 bg-amber-500/[0.06] text-amber-200'

  const what = full
    ? `This tree is full — ${used} of ${limit} people on the ${capacity.owner_plan} plan.`
    : `Nearly full — ${used} of ${limit} people on the ${capacity.owner_plan} plan.`

  const how = hidePurchases()
    ? (is_owner ? 'More room can be arranged from the website.' : "Ask the tree's keeper for more room.")
    : (is_owner ? 'Raise the limit from Profile → Subscription.' : "Ask the tree's keeper to raise the limit.")

  return (
    <div className={`flex items-start gap-2.5 mt-3 mb-1 px-3 py-2 rounded-xl border text-xs ${tone}`}>
      <span className="text-sm">{full ? '🚫' : '⚠'}</span>
      <div className="flex-1">
        <p><b>{what}</b> {full ? 'New people cannot be added until there is room.' : how}</p>
        <div className="h-1.5 rounded-full bg-white/[0.08] overflow-hidden mt-1.5">
          <div className={`h-full ${full ? 'bg-red-500/70' : 'bg-amber-500/70'}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  )
}
